import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ClipboardList, Loader2, RefreshCw, X, Check, ChefHat, Bike, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { formatBRL } from "../data/menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  novo: { label: "Novo", cls: "bg-emerald-400/15 text-emerald-300" },
  em_preparo: { label: "Em preparo", cls: "bg-amber-400/15 text-amber-300" },
  saiu_entrega: { label: "Saiu pra entrega", cls: "bg-cyan-400/15 text-cyan-300" },
  entregue: { label: "Entregue", cls: "bg-purple-400/20 text-purple-300" },
};

const buildNotifyUrl = (o) => {
  const digits = String(o.phone || "").replace(/\D/g, "");
  if (digits.length < 10) return null;
  const phone = digits.startsWith("55") ? digits : `55${digits}`;
  const items = o.items.map((i) => `${i.qty > 1 ? `${i.qty}x ` : ""}${i.title}`).join(", ");
  const link = `${window.location.origin}/pedido/${o.order_id}`;
  const msg = `Oi ${o.customer_name}! Seu pedido no Giga Açaí saiu pra entrega! 🛵\n\nPedido: ${items}\nTotal: ${formatBRL(o.total)}\n\nAcompanhe em tempo real:\n${link}\n\nQualquer coisa é só chamar aqui!`;
  return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
};

export const AdminPanel = ({ open, onClose }) => {
  const [orders, setOrders] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/orders`, { credentials: "include" });
      if (!r.ok) throw new Error();
      setOrders(await r.json());
    } catch {
      setOrders([]);
      toast.error("Não foi possível carregar os pedidos");
    }
  }, []);

  const setStatus = useCallback(
    async (orderId, status) => {
      try {
        const r = await fetch(`${API}/orders/${orderId}/status`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ status }),
        });
        if (!r.ok) throw new Error();
        toast.success("Status atualizado");
        load();
      } catch {
        toast.error("Não foi possível atualizar o status");
      }
    },
    [load]
  );

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={onClose}
          data-testid="admin-panel-backdrop"
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[88vh] w-full overflow-y-auto rounded-t-[2rem] border border-purple-500/30 bg-[#1E053B] p-6 sm:max-w-2xl sm:rounded-[2rem] sm:p-8"
            data-testid="admin-panel"
          >
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2.5 font-syne text-xl font-extrabold text-white">
                <ClipboardList className="h-5 w-5 text-amber-400" /> Pedidos recebidos
              </h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="admin-refresh-button"
                  onClick={load}
                  aria-label="Atualizar pedidos"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-purple-500/30 text-purple-200 transition-colors hover:border-emerald-400 hover:text-emerald-300"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  data-testid="admin-close-button"
                  onClick={onClose}
                  aria-label="Fechar painel"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-purple-500/30 text-purple-200 transition-colors hover:border-red-400 hover:text-red-400"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {orders === null ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-7 w-7 animate-spin text-amber-400" />
              </div>
            ) : orders.length === 0 ? (
              <p data-testid="admin-empty" className="py-16 text-center text-sm text-purple-300/60">
                Nenhum pedido recebido ainda.
              </p>
            ) : (
              <div className="mt-6 space-y-4">
                {orders.map((o, i) => (
                  <div
                    key={o.order_id}
                    data-testid={`admin-order-${i}`}
                    className="rounded-2xl border border-purple-500/20 bg-white/5 p-5"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-bold text-white">{o.customer_name}</p>
                      <span
                        data-testid={`admin-status-badge-${i}`}
                        className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${(STATUS_META[o.status] || STATUS_META.novo).cls}`}
                      >
                        {(STATUS_META[o.status] || STATUS_META.novo).label}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[11px] text-purple-300/50">
                      {new Date(o.created_at).toLocaleString("pt-BR")} ·{" "}
                      {o.mode === "entrega" ? "Entrega" : "Retirada"}
                      {o.mode === "entrega" && o.address ? ` · ${o.address}` : ""}
                      {o.distance_km ? ` (≈ ${o.distance_km} km)` : ""}
                      {o.phone ? ` · WhatsApp ${o.phone}` : ""}
                    </p>
                    <div className="mt-3 space-y-1">
                      {o.items.map((it, j) => (
                        <p key={j} className="text-xs text-purple-200/80">
                          <span className="font-semibold text-white">
                            {it.qty > 1 ? `${it.qty}x ` : ""}
                            {it.title}
                          </span>
                          {it.details && it.details.length > 0 && ` — ${it.details.join(", ")}`}
                        </p>
                      ))}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-purple-500/15 pt-3 text-xs text-purple-200/70">
                      <span>
                        Subtotal {formatBRL(o.subtotal)}
                        {o.mode === "entrega" &&
                          ` · Entrega ${o.fee === 0 ? "grátis" : formatBRL(o.fee)}`}
                        {o.spoons > 0 && ` · ${o.spoons} colher${o.spoons > 1 ? "es" : ""}`}
                        {o.payment ? ` · ${o.payment}` : ""}
                      </span>
                      <span className="font-syne text-base font-extrabold text-emerald-400">
                        {formatBRL(o.total)}
                      </span>
                    </div>
                    {o.notes && (
                      <p className="mt-2 text-[11px] italic text-purple-300/60">Obs: {o.notes}</p>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {o.status === "novo" && (
                        <button
                          type="button"
                          data-testid={`admin-status-preparo-${i}`}
                          onClick={() => setStatus(o.order_id, "em_preparo")}
                          className="flex items-center gap-1.5 rounded-full bg-amber-400 px-4 py-2 text-[11px] font-bold text-[#1E053B] transition-transform duration-200 hover:scale-105 active:scale-95"
                        >
                          <ChefHat className="h-3.5 w-3.5" /> Em preparo
                        </button>
                      )}
                      {o.status === "em_preparo" && (
                        <button
                          type="button"
                          data-testid={`admin-status-saiu-${i}`}
                          onClick={() => setStatus(o.order_id, "saiu_entrega")}
                          className="flex items-center gap-1.5 rounded-full bg-cyan-400 px-4 py-2 text-[11px] font-bold text-[#1E053B] transition-transform duration-200 hover:scale-105 active:scale-95"
                        >
                          <Bike className="h-3.5 w-3.5" /> Saiu pra entrega
                        </button>
                      )}
                      {o.status === "saiu_entrega" && (
                        <>
                          <button
                            type="button"
                            data-testid={`admin-status-entregue-${i}`}
                            onClick={() => setStatus(o.order_id, "entregue")}
                            className="flex items-center gap-1.5 rounded-full bg-emerald-400 px-4 py-2 text-[11px] font-bold text-[#1E053B] transition-transform duration-200 hover:scale-105 active:scale-95"
                          >
                            <Check className="h-3.5 w-3.5" strokeWidth={3} /> Entregue
                          </button>
                          {buildNotifyUrl(o) && (
                            <a
                              data-testid={`admin-notify-${i}`}
                              href={buildNotifyUrl(o)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 rounded-full bg-[#25D366] px-4 py-2 text-[11px] font-bold text-white transition-transform duration-200 hover:scale-105 active:scale-95"
                            >
                              <MessageCircle className="h-3.5 w-3.5" /> Avisar cliente no WhatsApp
                            </a>
                          )}
                        </>
                      )}
                      {o.status === "entregue" && (
                        <span
                          data-testid={`admin-status-done-${i}`}
                          className="flex items-center gap-1.5 text-[11px] font-bold text-purple-300/60"
                        >
                          <Check className="h-3.5 w-3.5" strokeWidth={3} /> Pedido concluído
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
